import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Subject,
  catchError,
  filter,
  firstValueFrom,
  map,
  take,
  takeUntil
} from 'rxjs';

import { ApiService } from '@core/api/api.service';
import { cartItemCount } from '@core/weight-quantity';
import { AuthService } from '@core/auth/auth.service';

import {
  BasketNeed,
  BasketNeedPolicy,
  BasketNeedWrite,
  BranchOption,
  CityOption,
  ProductSearchItem,
  ProductBranchPrice,
  ProductAlternativeMatch,
  ShoppingBasket,
  SmartBasketComparison,
  SmartNeedResolution
} from '@core/models/models';
import {
  LocalProfileService,
  LocalShoppingProfile
} from '@shared/services/local-profile.service';
import { BasketViewComponent } from './components/basket-view/basket-view.component';
import { PriceComparisonViewComponent } from './components/price-comparison-view/price-comparison-view.component';
import { GroupShareModalComponent } from './components/group-share-modal/group-share-modal.component';

import { LocationSelectorModalComponent } from '@shared/components/location-selector-modal/location-selector-modal.component';
import { ProductSearchComponent } from './components/product-search/product-search.component';

@Component({
  selector: 'app-shopping-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LocationSelectorModalComponent,
    ProductSearchComponent,
    BasketViewComponent,
    PriceComparisonViewComponent,
    GroupShareModalComponent
  ],
  templateUrl: './shopping-home.component.html',
  styleUrl: './shopping-home.component.css'
})
export class ShoppingHomeComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  readonly auth = inject(AuthService);
  private readonly localProfile = inject(LocalProfileService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly destroy$ = new Subject<void>();
  private processingJoinToken = false;


  cities: CityOption[] = [];
  selectedCity = '';
  citiesLoading = false;
  branches: BranchOption[] = [];
  selectedBranchId: number | null = null;
  branchesLoading = false;
  locationSettingsOpen = false;

  get selectedCityLabel(): string {
    const city = this.cities.find((c) => c.value === this.selectedCity);
    return city?.label ?? this.selectedCity;
  }

  get selectedBranch(): BranchOption | null {
    return this.branches.find((b) => b.id === this.selectedBranchId) ?? null;
  }

  get selectedBranchLabel(): string {
    const branch = this.selectedBranch;
    return branch ? `${branch.chainName} · ${branch.branchName}` : '';
  }

  get hasSelectedLocation(): boolean {
    return !!(this.selectedCity && this.selectedBranchId != null);
  }

  get basketOriginBranchMismatch(): boolean {
    return (
      this.basket?.branchId != null &&
      this.selectedBranchId != null &&
      this.basket.branchId !== this.selectedBranchId
    );
  }

  get basketOriginBranchLabel(): string {
    if (this.basketOriginBranch) {
      return `${this.basketOriginBranch.chainName} · ${this.basketOriginBranch.branchName}`;
    }
    return 'סניף מקורי';
  }

  openLocationSettings(): void {
    this.locationSettingsOpen = true;
  }

  closeLocationSettings(): void {
    this.locationSettingsOpen = false;
  }

  basket: ShoppingBasket | null = null;
  /** Phone bottom bar. Wider screens show search and the list together. Defaults to list (cart). */
  phonePanel: 'search' | 'list' = 'list';
  profile: LocalShoppingProfile = this.localProfile.getProfile();
  comparison: SmartBasketComparison | null = null;

  /** True when this device can usefully scan barcodes (secure + camera + phone-like). */
  cameraAvailable = false;

  basketLoading = false;
  compareLoading = false;
  profileBusy = false;
  readonly busyNeedIds = new Set<number>();
  readonly estimatingNeedIds = new Set<number>();
  readonly addingProductIds = new Set<number>();
  readonly catalogPrices = new Map<number, ProductBranchPrice>();
  readonly alternativesByProduct = new Map<number, ProductAlternativeMatch[]>();
  readonly loadingAlternatives = new Set<number>();
  readonly swappingNeedIds = new Set<number>();
  basketOriginBranch: BranchOption | null = null;
  loadingBasketBranch = false;
  readonly productIdsWithImages = new Set<number>();
  private readonly weightPhotoByNeed = new Map<number, number>();
  readonly brokenImageIds = new Set<number>();
  imagePopupProductId: number | null = null;
  errorMessage: string | null = null;
  statusMessage: string | null = null;
  shareGroupOpen = false;
  countBumped = false;
  private countBumpTimer: ReturnType<typeof setTimeout> | null = null;
  savingBasketName = false;
  basketNameSaved = 0;
  private promptedShareLogin = false;

  ngOnInit(): void {
    const requestedTab = this.route.snapshot.queryParamMap.get('tab');
    this.phonePanel = requestedTab === 'search' ? 'search' : 'list';

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const tab = params.get('tab');
        if (tab === 'search') {
          this.phonePanel = 'search';
        } else if (tab === 'cart' || tab === 'list') {
          this.phonePanel = 'list';
        }
      });

    this.refreshCameraAvailable();
    this.loadCities();
    this.loadStoredBasket();

    if (this.localProfile.consumeOpenLocationSettingsRequest()) {
      this.locationSettingsOpen = true;
    }
    this.localProfile.openLocationSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.openLocationSettings();
      });

    const incomingJoinToken = this.route.snapshot.queryParamMap.get('joinToken');
    const incomingBasketId = this.route.snapshot.queryParamMap.get('basketId');
    if (incomingJoinToken) {
      try {
        sessionStorage.setItem('commpare_pending_join_token', incomingJoinToken);
        localStorage.setItem('commpare_pending_join_token', incomingJoinToken);
      } catch {}
    }
    if (incomingBasketId) {
      try {
        sessionStorage.setItem('commpare_pending_basket_id', incomingBasketId);
        localStorage.setItem('commpare_pending_basket_id', incomingBasketId);
      } catch {}
    }
    if (this.route.snapshot.queryParamMap.get('page') === 'store-list') {
      try {
        sessionStorage.setItem('commpare_pending_share_page', 'store-list');
        localStorage.setItem('commpare_pending_share_page', 'store-list');
      } catch {}
    }

    this.auth.sessionChecked$
      .pipe(
        filter((checked) => Boolean(checked)),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const queryParams = this.route.snapshot.queryParamMap;
        const requestedTab = queryParams.get('tab');
        const requestedBasketId = queryParams.get('basketId') || this.getPendingBasketId();
        const joinToken = queryParams.get('joinToken') || this.getPendingJoinToken();
        const sharePage = queryParams.get('page') || this.getPendingSharePage();
        const isShareOrCartTarget =
          requestedTab === 'cart' ||
          requestedTab === 'list' ||
          !!requestedBasketId ||
          !!joinToken ||
          sharePage === 'store-list' ||
          queryParams.get('login') === '1' ||
          queryParams.get('login') === 'true';

        if (isShareOrCartTarget && !this.promptedShareLogin) {
          this.promptedShareLogin = true;
          if (!this.auth.isLoggedIn) {
            this.auth.openLoginModal();
          } else {
            const basketIdNum = requestedBasketId ? Number(requestedBasketId) : null;
            this.handlePendingShare(basketIdNum);
          }
        }
      });

    this.auth.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          const requestedBasketId = this.route.snapshot.queryParamMap.get('basketId') || this.getPendingBasketId();
          const basketIdNum = requestedBasketId ? Number(requestedBasketId) : null;
          const joinToken = this.route.snapshot.queryParamMap.get('joinToken') || this.getPendingJoinToken();

          if (joinToken || this.isStoreListShare()) {
            this.handlePendingShare(basketIdNum);
            return;
          }

          if (basketIdNum && !isNaN(basketIdNum) && user.currentBasketId !== basketIdNum) {
            this.auth.selectBasket(basketIdNum).subscribe({
              next: () => {
                this.cleanupShareQueryParams();
                this.loadStoredBasket();
              },
              error: () => {
                this.cleanupShareQueryParams();
                if (user.currentBasketId) {
                  this.localProfile.setShoppingBasketId(user.currentBasketId);
                  this.loadStoredBasket();
                }
              }
            });
            return;
          }

          if (user.currentBasketId && (!this.basket || this.basket.id !== user.currentBasketId)) {
            this.localProfile.setShoppingBasketId(user.currentBasketId);
            this.loadStoredBasket();
          } else if (!user.currentBasketId) {
            this.basket = null;
            this.comparison = null;
          }
        }
      });
  }

  openLoginModal(): void {
    this.auth.openLoginModal();
  }

  get isBasketClosed(): boolean {
    return !!(this.basket?.isClosed || this.basket?.closedAt);
  }

  async reopenCurrentBasket(): Promise<void> {
    if (!this.basket) return;
    try {
      this.basketLoading = true;
      const updated = await firstValueFrom(this.auth.openBasket(this.basket.id));
      this.basket = updated;
      this.statusMessage = 'הסל נפתח מחדש לעריכה.';
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.basketLoading = false;
    }
  }

  async saveBasketName(newName: string): Promise<void> {
    if (!this.basket || this.savingBasketName) return;
    const trimmed = newName.trim();
    if (!trimmed || trimmed === this.basket.name) return;

    this.savingBasketName = true;
    try {
      const updated = await firstValueFrom(this.api.renameShoppingBasket(this.basket.id, trimmed));
      this.basket = {
        ...this.basket,
        name: updated.name
      };
      this.basketNameSaved++;
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.savingBasketName = false;
    }
  }

  toggleBasketGroupShare(): void {
    if (!this.auth.isLoggedIn) {
      this.auth.openLoginModal();
      return;
    }
    this.shareGroupOpen = true;
  }

  closeShareGroup(): void {
    this.shareGroupOpen = false;
  }

  onBasketShared(updated: ShoppingBasket): void {
    this.basket = updated;
    this.localProfile.setShoppingBasketId(updated.id);
  }

  /** Total quantity of items in the cart (sum of quantities of all needs). */
  get totalCartItemsCount(): number {
    if (!this.basket?.needs || this.basket.needs.length === 0) {
      return 0;
    }
    return cartItemCount(this.basket.needs);
  }

  private triggerCountBump(): void {
    this.countBumped = true;
    if (this.countBumpTimer != null) {
      clearTimeout(this.countBumpTimer);
    }
    this.countBumpTimer = setTimeout(() => {
      this.countBumped = false;
      this.countBumpTimer = null;
    }, 400);
  }


  private refreshCameraAvailable(): void {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      this.cameraAvailable = false;
      return;
    }

    const hasCameraApi =
      window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;
    // Barcode scan is aimed at in-store phone use; hide on desktop mouse UIs.
    const phoneLike =
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(max-width: 768px)').matches;

    this.cameraAvailable = hasCameraApi && phoneLike;

    if (!hasCameraApi || !navigator.mediaDevices.enumerateDevices) {
      return;
    }

    void navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const hasVideo = devices.some((d) => d.kind === 'videoinput');
        // Only hide when we know there is no camera. Empty lists often mean
        // permission has not been granted yet — keep the phone-like API gate.
        if (devices.length > 0 && !hasVideo) {
          this.cameraAvailable = false;
        }
      })
      .catch(() => {
        // keep API + phone-like result
      });
  }

  ngOnDestroy(): void {
    if (this.countBumpTimer != null) {
      clearTimeout(this.countBumpTimer);
      this.countBumpTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }



  onCityChange(city: string): void {
    this.selectedCity = city;
    this.localProfile.setSelectedCity(city);
    this.selectedBranchId = null;
    this.localProfile.setSelectedBranchId(null);
    this.branches = [];
    this.branchesLoading = false;
    this.comparison = null;
    this.catalogPrices.clear();
    if (city) this.loadBranches(city);
  }

  onBranchChange(branchId: number | null): void {
    this.selectedBranchId = branchId == null ? null : Number(branchId);
    this.localProfile.setSelectedBranchId(this.selectedBranchId);
    this.comparison = null;
    this.alternativesByProduct.clear();
    this.loadingAlternatives.clear();

    if (this.auth.isLoggedIn) {
      this.auth.updateProfile({
        preferredCity: this.selectedCity || null,
        preferredBranchId: this.selectedBranchId
      }).catch(() => {});
    }

    if (this.basket && this.selectedBranchId != null) {
      this.basket = { ...this.basket, branchId: this.selectedBranchId };
      this.basketOriginBranch = this.selectedBranch;
      this.api.setBasketBranch(this.basket.id, this.selectedBranchId).subscribe({
        next: (updated) => {
          if (this.basket && this.basket.id === updated.id) {
            this.basket = { ...this.basket, branchId: updated.branchId };
          }
        },
        error: () => {}
      });
    }

    this.refreshCatalogPrices();
  }

  async addProduct(product: ProductSearchItem, quantity = 1, weightPhotoId?: number): Promise<void> {
    if (this.isBasketClosed) {
      this.errorMessage = 'הסל סגור לקניות. יש לפתוח אותו מחדש כדי להוסיף מוצרים.';
      return;
    }
    if (!this.selectedBranchId) {
      this.errorMessage = 'יש לבחור סניף פיזי לפני הוספת מוצר.';
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      this.errorMessage = 'יש להזין כמות גדולה מאפס.';
      return;
    }

    this.addingProductIds.add(product.id);
    this.clearMessages();
    try {
      const basket = await this.ensureBasket();

      // If the product is already in the basket, increment its quantity
      const existingNeed = basket.needs.find(
        (need) => need.lockedProductId != null && need.lockedProductId === product.id
      );
      if (existingNeed) {
        await this.changeQuantity(existingNeed, existingNeed.quantity + quantity, weightPhotoId);
        if (product.prices[0]) {
          this.catalogPrices.set(product.id, product.prices[0]);
        }
        this.rememberProductImage(product.id, product.hasImage);
        this.triggerCountBump();
        this.statusMessage = 'הכמות עודכנה ברשימה.';
        return;
      }

      const created = await firstValueFrom(
        this.api.createBasketNeed(basket.id, {
          requestedText: product.displayName || product.nameHe || product.nameRaw,
          quantity,
          policy: 'exact',
          lockedProductId: product.id,
          unitSize: null,
          unitUom: null,
          packCount: null,
          approvedProductIds: [],
          weightPhotoId
        })
      );
      this.basket = {
        ...basket,
        needCount: basket.needs.length + 1,
        needs: [...basket.needs, created]
      };
      if (product.prices[0]) {
        this.catalogPrices.set(product.id, product.prices[0]);
      }
      this.rememberProductImage(product.id, product.hasImage);
      if (weightPhotoId) this.weightPhotoByNeed.set(created.id, weightPhotoId);
      this.comparison = null;
      this.triggerCountBump();
      this.statusMessage = 'המוצר נוסף לרשימה.';
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.addingProductIds.delete(product.id);
    }
  }

  async adjustSearchQuantity(
    product: ProductSearchItem,
    quantity: number,
    weightPhotoId?: number
  ): Promise<void> {
    const need = this.basket?.needs.find((item) => item.lockedProductId === product.id);
    if (!need) return;
    await this.changeQuantity(need, quantity, weightPhotoId);
  }

  async changeQuantity(need: BasketNeed, quantity: number, weightPhotoId?: number): Promise<void> {
    if (!Number.isFinite(quantity) || quantity <= 0 || quantity === need.quantity) return;
    if (weightPhotoId) this.weightPhotoByNeed.set(need.id, weightPhotoId);
    await this.updateNeed(need, {
      quantity,
      weightPhotoId: weightPhotoId ?? this.weightPhotoByNeed.get(need.id)
    });
    this.triggerCountBump();
  }

  async estimateNeedWeight(need: BasketNeed, frameJpeg: string): Promise<void> {
    if (need.lockedProductId == null || this.estimatingNeedIds.has(need.id)) return;
    this.estimatingNeedIds.add(need.id);
    try {
      const result = await firstValueFrom(
        this.api.estimateBagWeight(need.lockedProductId, need.requestedText, frameJpeg)
      );
      if (!result.readable || result.kilograms == null) {
        this.errorMessage = 'לא הצלחנו להעריך את משקל השקית. נסו שוב או הקלידו את המשקל.';
        return;
      }
      this.weightPhotoByNeed.set(need.id, result.photoId);
      await this.changeQuantity(need, result.kilograms, result.photoId);
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.estimatingNeedIds.delete(need.id);
    }
  }

  async removeNeed(need: BasketNeed): Promise<void> {
    if (!this.basket || this.busyNeedIds.has(need.id)) return;
    if (this.isBasketClosed) {
      this.errorMessage = 'הסל סגור לקניות. יש לפתוח אותו מחדש כדי לערוך אותו.';
      return;
    }
    this.busyNeedIds.add(need.id);
    this.clearMessages();
    try {
      await firstValueFrom(this.api.deleteBasketNeed(this.basket.id, need.id));
      const remainingNeeds = this.basket.needs.filter((item) => item.id !== need.id);
      this.basket = {
        ...this.basket,
        needCount: remainingNeeds.length,
        needs: remainingNeeds
      };
      this.profile = this.localProfile.forgetNeed(need.id);
      this.comparison = null;
      this.triggerCountBump();
      this.statusMessage = 'הצורך הוסר.';
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.busyNeedIds.delete(need.id);
    }
  }

  async compare(): Promise<void> {
    if (!this.basket || this.basket.needs.length === 0) {
      this.errorMessage = 'יש להוסיף לפחות צורך אחד לפני ההשוואה.';
      return;
    }
    if (!this.selectedCity) {
      this.errorMessage = 'יש לבחור עיר לפני ההשוואה.';
      return;
    }
    if (!this.selectedBranchId) {
      this.errorMessage = 'יש לבחור סניף פיזי לפני ההשוואה.';
      return;
    }

    this.compareLoading = true;
    this.clearMessages();
    try {
      this.comparison = await firstValueFrom(
        this.api.smartCompareBasket(this.basket.id, {
          city: this.selectedCity,
          profile: this.localProfile.toApiProfile(
            this.profile,
            this.basket.needs.map((need) => need.id)
          )
        })
      );
      if (this.comparison.branches.length === 0) {
        this.statusMessage = 'לא נמצאו סניפים עם נתוני מחיר בעיר שנבחרה.';
      }
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.compareLoading = false;
    }
  }

  async approveSuggestion(resolution: SmartNeedResolution): Promise<void> {
    if (
      !resolution.approvalRequired ||
      resolution.selectedProductId == null ||
      this.profileBusy
    ) {
      return;
    }
    this.profileBusy = true;
    this.clearMessages();
    this.profile = this.localProfile.approveAlternative(
      resolution.needId,
      resolution.selectedProductId
    );
    this.statusMessage = 'החלופה אושרה ונשמרה במכשיר הזה.';
    try {
      await this.compare();
    } finally {
      this.profileBusy = false;
    }
  }

  async lockExact(resolution: SmartNeedResolution): Promise<void> {
    if (!this.basket || resolution.selectedProductId == null) return;
    const need = this.basket.needs.find((item) => item.id === resolution.needId);
    if (!need) return;

    const updated = await this.updateNeed(need, {
      policy: 'exact',
      lockedProductId: resolution.selectedProductId
    });
    if (updated) {
      this.statusMessage = 'המוצר ננעל כהתאמה מדויקת.';
      await this.compare();
    }
  }

  @HostListener('document:keydown.escape')
  closeImagePopupOnEscape(): void {
    if (this.imagePopupProductId !== null) {
      this.closeImagePopup();
      return;
    }
    if (this.shareGroupOpen) {
      this.closeShareGroup();
      return;
    }
    if (this.locationSettingsOpen) {
      this.closeLocationSettings();
      return;
    }
  }

  productImageUrl(productId: number): string {
    return this.api.productImageUrl(productId);
  }

  hasProductPhoto(productId: number | null | undefined): boolean {
    return (
      productId != null &&
      this.productIdsWithImages.has(productId) &&
      !this.brokenImageIds.has(productId)
    );
  }

  markImageBroken(productId: number): void {
    this.brokenImageIds.add(productId);
    if (this.imagePopupProductId === productId) {
      this.closeImagePopup();
    }
  }

  openImagePopup(productId: number): void {
    this.imagePopupProductId = productId;
  }

  closeImagePopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.imagePopupProductId = null;
  }



  private loadCities(): void {
    this.citiesLoading = true;
    this.api.listCities().subscribe({
      next: (response) => {
        this.cities = response.cities;
        const queryCity = this.route.snapshot.queryParamMap.get('city')?.trim();
        const storedCity = this.localProfile.getSelectedCity();
        const userCity = this.auth.currentUser?.preferredCity;
        const cityToUse = (queryCity && this.cities.some((c) => c.value === queryCity))
          ? queryCity
          : (storedCity && this.cities.some((c) => c.value === storedCity))
          ? storedCity
          : (userCity && this.cities.some((c) => c.value === userCity))
          ? userCity
          : '';

        if (cityToUse) {
          this.selectedCity = cityToUse;
          this.localProfile.setSelectedCity(cityToUse);
          this.loadBranches(cityToUse);
        } else {
          this.locationSettingsOpen = true;
        }
        this.citiesLoading = false;
      },
      error: (error) => {
        this.citiesLoading = false;
        this.errorMessage = this.readError(error);
      }
    });
  }

  private loadBranches(city: string): void {
    this.branchesLoading = true;
    this.api.listPhysicalBranches(city).subscribe({
      next: (response) => {
        if (this.selectedCity !== city) return;

        this.branches = response.branches;
        const queryBranchRaw = this.route.snapshot.queryParamMap.get('branchId');
        const queryBranchId = queryBranchRaw ? Number(queryBranchRaw) : null;
        const storedBranchId = this.localProfile.getSelectedBranchId();
        const userBranchId = this.auth.currentUser?.preferredBranchId;
        const branchToUse = (queryBranchId && this.branches.some((b) => b.id === queryBranchId))
          ? queryBranchId
          : (storedBranchId && this.branches.some((b) => b.id === storedBranchId))
          ? storedBranchId
          : (userBranchId && this.branches.some((b) => b.id === userBranchId))
          ? userBranchId
          : null;

        if (branchToUse != null) {
          this.selectedBranchId = branchToUse;
          this.localProfile.setSelectedBranchId(branchToUse);
        } else {
          this.selectedBranchId = null;
          this.localProfile.setSelectedBranchId(null);
          this.locationSettingsOpen = true;
        }
        this.branchesLoading = false;
        this.refreshCatalogPrices();
      },
      error: (error) => {
        if (this.selectedCity !== city) return;
        this.branches = [];
        this.selectedBranchId = null;
        this.localProfile.setSelectedBranchId(null);
        this.branchesLoading = false;
        this.catalogPrices.clear();
        this.errorMessage = this.readError(error);
      }
    });
  }


  private refreshCatalogPrices(): void {
    const branchId = this.selectedBranchId;
    const productIds = [
      ...new Set(
        (this.basket?.needs ?? [])
          .map((need) => need.lockedProductId)
          .filter((id): id is number => id != null)
      )
    ];
    if (branchId == null || productIds.length === 0) return;

    this.api.listBranchProductPrices(branchId, productIds).subscribe({
      next: (response) => {
        if (this.selectedBranchId !== branchId) return;
        this.catalogPrices.clear();
        const pricedIds = new Set<number>();
        for (const item of response.items) {
          this.catalogPrices.set(item.productId, item);
          this.rememberProductImage(item.productId, item.hasImage);
          pricedIds.add(item.productId);
        }
        const missingIds = productIds.filter((id) => !pricedIds.has(id));
        if (missingIds.length > 0) {
          this.loadMissingProductAlternatives(branchId, missingIds);
        }
      }
    });
  }

  private loadMissingProductAlternatives(branchId: number, productIds: number[]): void {
    for (const pid of productIds) {
      if (this.alternativesByProduct.has(pid) || this.loadingAlternatives.has(pid)) {
        continue;
      }
      this.loadingAlternatives.add(pid);
      this.api.getProductAlternatives(branchId, pid, 3).subscribe({
        next: (res) => {
          this.loadingAlternatives.delete(pid);
          if (this.selectedBranchId !== branchId) return;
          this.alternativesByProduct.set(pid, res.alternatives);
          for (const alt of res.alternatives) {
            this.rememberProductImage(alt.productId, alt.hasImage);
          }
        },
        error: () => {
          this.loadingAlternatives.delete(pid);
        }
      });
    }
  }

  async swapNeedWithAlternative(need: BasketNeed, alt: ProductAlternativeMatch): Promise<void> {
    if (this.swappingNeedIds.has(need.id) || this.isBasketClosed) return;
    this.swappingNeedIds.add(need.id);
    this.clearMessages();
    try {
      const targetName = alt.displayName || alt.nameHe;
      const updated = await this.updateNeed(need, {
        requestedText: targetName,
        lockedProductId: alt.productId,
        policy: 'exact'
      });
      if (updated && this.selectedBranchId != null) {
        this.catalogPrices.set(alt.productId, {
          branchId: this.selectedBranchId,
          branchName: this.selectedBranchLabel,
          price: alt.price,
          unitPrice: alt.unitPrice,
          unitOfMeasure: alt.unitOfMeasure ?? null,
          currency: alt.currency || 'ILS',
          validFrom: new Date().toISOString(),
          promoPrice: alt.promoPrice ?? null,
          promoLabel: alt.promoLabel ?? null
        });
        this.rememberProductImage(alt.productId, alt.hasImage);
        this.triggerCountBump();
        this.statusMessage = `המוצר הוחלף בהצלחה ל-${targetName}!`;
      }
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.swappingNeedIds.delete(need.id);
    }
  }

  async switchToBasketBranch(): Promise<void> {
    if (!this.basket?.branchId) return;
    const targetBranchId = this.basket.branchId;
    try {
      let targetBranch = this.basketOriginBranch;
      if (!targetBranch || targetBranch.id !== targetBranchId) {
        targetBranch = await firstValueFrom(this.api.getBranch(targetBranchId));
        this.basketOriginBranch = targetBranch;
      }
      if (targetBranch) {
        if (this.selectedCity !== targetBranch.city) {
          this.selectedCity = targetBranch.city;
          this.localProfile.setSelectedCity(targetBranch.city);
          const res = await firstValueFrom(this.api.listPhysicalBranches(targetBranch.city));
          this.branches = res.branches;
        }
        this.onBranchChange(targetBranch.id);
      }
    } catch {
      this.errorMessage = 'לא ניתן היה לעבור לסניף המקורי של הסל.';
    }
  }

  private loadStoredBasket(): void {
    const id = this.localProfile.getShoppingBasketId();
    if (id == null) return;

    this.basketLoading = true;
    this.api.getShoppingBasket(id).subscribe({
      next: (basket) => {
        this.basket = basket;
        this.basketLoading = false;
        this.syncBasketBranchOrigin(basket);
        this.refreshCatalogPrices();
      },
      error: () => {
        this.basketLoading = false;
        if (this.basket) return;
        this.localProfile.clearShoppingBasketId();
      }
    });
  }

  private syncBasketBranchOrigin(basket: ShoppingBasket): void {
    if (!basket.branchId) {
      this.basketOriginBranch = null;
      return;
    }

    if (this.selectedBranchId == null) {
      this.api.getBranch(basket.branchId).subscribe({
        next: (branch) => {
          this.basketOriginBranch = branch;
          if (this.selectedBranchId == null) {
            this.selectedCity = branch.city;
            this.localProfile.setSelectedCity(branch.city);
            this.selectedBranchId = branch.id;
            this.localProfile.setSelectedBranchId(branch.id);
            this.loadBranches(branch.city);
          }
        },
        error: () => {}
      });
      return;
    }

    if (basket.branchId === this.selectedBranchId) {
      this.basketOriginBranch = this.selectedBranch;
    } else {
      this.api.getBranch(basket.branchId).subscribe({
        next: (branch) => {
          this.basketOriginBranch = branch;
        },
        error: () => {}
      });
    }
  }

  private async ensureBasket(): Promise<ShoppingBasket> {
    if (this.basket) return this.basket;
    const defaultGroup = this.auth.currentUser?.defaultGroupId;
    const basket = await firstValueFrom(
      this.api.createShoppingBasket('רשימת הקניות שלי', defaultGroup ?? null, this.selectedBranchId)
    );
    this.localProfile.setShoppingBasketId(basket.id);
    if (basket.claimToken) {
      this.localProfile.setShoppingBasketClaimToken(basket.claimToken);
    }
    this.basket = basket;
    this.syncBasketBranchOrigin(basket);
    return basket;
  }

  private async updateNeed(
    need: BasketNeed,
    changes: Partial<BasketNeedWrite>
  ): Promise<BasketNeed | null> {
    if (!this.basket || this.busyNeedIds.has(need.id)) return null;
    if (this.isBasketClosed) {
      this.errorMessage = 'הסל סגור לקניות. יש לפתוח אותו מחדש כדי לערוך אותו.';
      return null;
    }
    this.busyNeedIds.add(need.id);
    this.clearMessages();
    try {
      const updated = await firstValueFrom(
        this.api.updateBasketNeed(this.basket.id, need.id, {
          ...this.toWrite(need),
          ...changes
        })
      );
      this.basket = {
        ...this.basket,
        needs: this.basket.needs.map((item) => (item.id === updated.id ? updated : item))
      };
      this.comparison = null;
      return updated;
    } catch (error) {
      this.errorMessage = this.readError(error);
      return null;
    } finally {
      this.busyNeedIds.delete(need.id);
    }
  }

  private rememberProductImage(productId: number, hasImage: boolean | undefined): void {
    if (hasImage === true) this.productIdsWithImages.add(productId);
  }

  private toWrite(need: BasketNeed): BasketNeedWrite {
    return {
      requestedText: need.requestedText,
      quantity: need.quantity,
      policy: need.policy,
      lockedProductId: need.lockedProductId,
      unitSize: need.unitSize,
      unitUom: need.unitUom,
      packCount: need.packCount,
      approvedProductIds: [...need.approvedProductIds],
      listLabel: need.listLabel ?? null
    };
  }

  private clearMessages(): void {
    this.errorMessage = null;
    this.statusMessage = null;
  }

  private getPendingJoinToken(): string | null {
    try {
      return sessionStorage.getItem('commpare_pending_join_token') || localStorage.getItem('commpare_pending_join_token');
    } catch {
      return null;
    }
  }

  private getPendingBasketId(): string | null {
    try {
      return sessionStorage.getItem('commpare_pending_basket_id') || localStorage.getItem('commpare_pending_basket_id');
    } catch {
      return null;
    }
  }

  private getPendingSharePage(): string | null {
    try {
      const page = sessionStorage.getItem('commpare_pending_share_page') || localStorage.getItem('commpare_pending_share_page');
      return page === 'store-list' ? page : null;
    } catch {
      return null;
    }
  }

  private isStoreListShare(): boolean {
    return this.route.snapshot.queryParamMap.get('page') === 'store-list' || this.getPendingSharePage() === 'store-list';
  }

  private openSharedStoreList(basketIdNum: number | null): void {
    if (basketIdNum != null && !isNaN(basketIdNum)) {
      this.localProfile.setStoreListBasketId(basketIdNum);
    }
    this.clearPendingShareStorage();
    void this.router.navigate(['/'], { replaceUrl: true });
  }

  private clearPendingShareStorage(): void {
    try {
      sessionStorage.removeItem('commpare_pending_join_token');
      sessionStorage.removeItem('commpare_pending_basket_id');
      sessionStorage.removeItem('commpare_pending_share_page');
      localStorage.removeItem('commpare_pending_join_token');
      localStorage.removeItem('commpare_pending_basket_id');
      localStorage.removeItem('commpare_pending_share_page');
    } catch {}
  }

  private handlePendingShare(basketIdNum: number | null): void {
    const storeList = this.isStoreListShare();
    const token = this.route.snapshot.queryParamMap.get('joinToken') || this.getPendingJoinToken();
    if (token) {
      if (this.processingJoinToken) return;
      this.processingJoinToken = true;

      this.auth.joinGroupByToken(token).subscribe({
        next: (group) => {
          this.processingJoinToken = false;
          if (storeList) {
            this.openSharedStoreList(basketIdNum);
            return;
          }
          this.clearPendingShareStorage();
          this.cleanupShareQueryParams();
          this.statusMessage = `הצטרפת לקבוצת הקניות "${group.name}" בהצלחה!`;
          if (basketIdNum && !isNaN(basketIdNum)) {
            this.auth.selectBasket(basketIdNum).subscribe({
              next: () => this.loadStoredBasket(),
              error: (err) => {
                console.warn('Could not select basket after joining group:', err);
                this.loadStoredBasket();
              }
            });
          } else {
            this.loadStoredBasket();
          }
        },
        error: (err) => {
          this.processingJoinToken = false;
          this.clearPendingShareStorage();
          this.cleanupShareQueryParams();
          this.statusMessage = err?.error?.error || 'קישור ההזמנה פג תוקף או שאינו תקין.';
        }
      });
      return;
    }

    if (storeList) {
      this.openSharedStoreList(basketIdNum);
      return;
    }

    if (basketIdNum && !isNaN(basketIdNum) && (!this.basket || this.basket.id !== basketIdNum)) {
      this.clearPendingShareStorage();
      this.auth.selectBasket(basketIdNum).subscribe({
        next: () => {
          this.cleanupShareQueryParams();
          this.loadStoredBasket();
        },
        error: (err) => {
          console.warn('Could not select requested basket:', err);
          this.cleanupShareQueryParams();
        }
      });
    }
  }

  private cleanupShareQueryParams(): void {
    const currentParams = { ...this.route.snapshot.queryParams };
    if (
      currentParams['joinToken'] !== undefined ||
      currentParams['basketId'] !== undefined ||
      currentParams['page'] !== undefined ||
      currentParams['login'] !== undefined
    ) {
      delete currentParams['joinToken'];
      delete currentParams['basketId'];
      delete currentParams['page'];
      delete currentParams['login'];
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: currentParams,
        queryParamsHandling: '',
        replaceUrl: true
      });
    }
  }

  private readError(error: unknown): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const responseBody = (error as { error?: unknown }).error;
      if (
        responseBody &&
        typeof responseBody === 'object' &&
        'error' in responseBody &&
        typeof (responseBody as { error?: unknown }).error === 'string'
      ) {
        return (responseBody as { error: string }).error;
      }
    }
    return 'לא הצלחנו להשלים את הפעולה. אפשר לנסות שוב.';
  }
}
